<template>
  <div class="material-extract-page">
    <site-header @auth-updated="handleAuthUpdated" />
    <div class="material-extract-body">
      <workspace-expanded-nav class="material-extract-nav" />
      <div class="material-extract-content">
        <header class="extract-header">
          <div class="extract-brand">
            <i class="el-icon-files" aria-hidden="true"></i>
            <strong>素材提取</strong>
            <span class="header-divider"></span>
            <span>从 Word / PDF 文档中提取目录、文本、表格、图片、公式、流程图等文件要素</span>
          </div>
          <el-button
            class="export-button"
            icon="el-icon-download"
            :disabled="!canExport"
            :loading="exporting"
            @click="exportAll"
          >批量导出</el-button>
        </header>

        <div class="extract-workspace">
      <aside class="upload-panel">
        <h2>上传文档</h2>
        <div
          class="upload-dropzone"
          :class="{ dragging: dragActive, occupied: currentTask }"
          role="button"
          tabindex="0"
          aria-label="上传 Word 或 PDF 文档"
          @click="chooseFile"
          @keydown.enter.prevent="chooseFile"
          @keydown.space.prevent="chooseFile"
          @dragenter.prevent="dragActive = true"
          @dragover.prevent="dragActive = true"
          @dragleave.prevent="dragActive = false"
          @drop.prevent="handleDrop"
        >
          <input
            ref="fileInput"
            class="file-input"
            type="file"
            accept=".doc,.docx,.pdf"
            @change="handleFileChange"
          />
          <template v-if="currentTask">
            <img class="current-file-icon" :src="fileIcon(currentTask.fileExtension)" alt="" />
            <div class="current-file-name" :title="currentTask.fileName">{{ currentTask.fileName }}</div>
            <div class="current-file-meta">{{ formatFileSize(currentTask.fileSize) }} · {{ statusText(currentTask.status) }}</div>
            <div class="replace-file">点击或拖拽上传新文档</div>
          </template>
          <template v-else>
            <i class="el-icon-document upload-document-icon" aria-hidden="true"></i>
            <i class="el-icon-upload2 upload-arrow-icon" aria-hidden="true"></i>
            <div class="upload-title">点击或拖拽上传</div>
            <div class="upload-tip">支持 .docx / .doc / .pdf 格式<br />文件大小不超过 100MB</div>
          </template>
          <div v-if="uploading" class="upload-mask">
            <i class="el-icon-loading" aria-hidden="true"></i>
            <span>正在上传文档...</span>
          </div>
        </div>

        <section class="supported-card">
          <h3>支持提取的要素类型</h3>
          <ul>
            <li v-for="item in elementTypes" :key="item.type">
              <i :class="[item.icon, item.iconClass]" aria-hidden="true"></i>
              <span>{{ item.label }}</span>
            </li>
          </ul>
        </section>

        <section class="overview-card" :class="{ muted: !currentTask }">
          <h3>提取概览</h3>
          <div class="overview-total">{{ currentTask ? currentTask.totalElements || 0 : 0 }}</div>
          <div class="overview-label">共提取要素</div>
          <div class="overview-grid">
            <div><strong>{{ currentTask ? currentTask.successElements || 0 : 0 }}</strong><span>成功</span></div>
            <div><strong>{{ currentTask ? currentTask.failedElements || 0 : 0 }}</strong><span>失败</span></div>
          </div>
        </section>
      </aside>

      <main class="result-panel">
        <div class="result-heading">
          <div>
            <h2>提取结果</h2>
            <span v-if="currentTask" class="active-file" :title="currentTask.fileName">{{ currentTask.fileName }}</span>
          </div>
          <div v-if="currentTask && isActiveStatus(currentTask.status)" class="task-actions">
            <el-button type="text" @click="cancelTask">取消任务</el-button>
          </div>
          <div v-else-if="currentTask && canRetry" class="task-actions">
            <el-button type="text" @click="retryTask">重新提取</el-button>
          </div>
        </div>

        <div v-if="currentTask" class="filter-bar" role="tablist" aria-label="素材类型筛选">
          <button
            v-for="filter in filters"
            :key="filter.type"
            type="button"
            role="tab"
            :aria-selected="selectedType === filter.type ? 'true' : 'false'"
            :class="{ active: selectedType === filter.type }"
            @click="selectType(filter.type)"
          >{{ filter.label }}<span v-if="filter.count !== null">{{ filter.count }}</span></button>
        </div>

        <div class="result-body" tabindex="0" aria-label="提取结果列表">
          <div v-if="!currentTask" class="empty-state">
            <i class="el-icon-files" aria-hidden="true"></i>
            <h3>请先在左侧上传 Word 或 PDF 文档</h3>
            <p>AI 将按文档顺序提取所有文件要素并自动命名</p>
          </div>

          <div v-else-if="isActiveStatus(currentTask.status)" class="progress-state">
            <div class="progress-icon"><i class="el-icon-loading" aria-hidden="true"></i></div>
            <h3>{{ currentTask.progressMessage || '正在提取文档内容' }}</h3>
            <p>可以查看其他历史记录，当前任务会继续在后台处理</p>
            <el-progress :percentage="currentTask.progress || 0" :stroke-width="8" color="#17b77b"></el-progress>
          </div>

          <div v-else-if="isFailedStatus(currentTask.status)" class="empty-state failed-state">
            <i class="el-icon-warning-outline" aria-hidden="true"></i>
            <h3>{{ statusText(currentTask.status) }}</h3>
            <p>{{ currentTask.errorMessage || currentTask.progressMessage || '本次任务未生成可用结果' }}</p>
            <el-button v-if="canRetry" type="primary" size="small" @click="retryTask">重新提取</el-button>
          </div>

          <div v-else-if="elementsLoading && !elements.length" class="result-loading">
            <i class="el-icon-loading" aria-hidden="true"></i>
            <span>正在载入提取结果...</span>
          </div>

          <div v-else-if="!elements.length" class="empty-state compact-empty">
            <i class="el-icon-document-delete" aria-hidden="true"></i>
            <h3>当前分类暂无提取结果</h3>
            <p>可切换上方类型查看其他素材</p>
          </div>

          <div v-else class="element-list">
            <article v-for="element in elements" :key="element.elementId" class="element-card">
              <div class="element-card-head">
                <div class="element-title-wrap">
                  <span class="type-badge" :class="`type-${String(element.type || '').toLowerCase()}`">
                    <i :class="elementTypeMeta(element.type).icon" aria-hidden="true"></i>
                    {{ elementTypeMeta(element.type).label }}
                  </span>
                  <h3 v-if="element.type !== 'REFERENCE'" :title="element.title">{{ element.title || `${elementTypeMeta(element.type).label}-${element.orderNo || ''}` }}</h3>
                  <div v-else class="reference-title-row">
                    <button
                      type="button"
                      class="reference-title"
                      :title="`聚合检索：${referenceTitle(element)}`"
                      @click="openReferenceSearch('scholar', element)"
                    >{{ referenceTitle(element) }}</button>
                    <span class="reference-search-links" aria-label="参考文献检索入口">
                      <button type="button" @click="openReferenceSearch('wanfang', element)">万方</button>
                      <button type="button" @click="openReferenceSearch('cnki', element)">知网</button>
                      <button type="button" @click="openReferenceSearch('scholar', element)">聚合检索</button>
                    </span>
                  </div>
                </div>
                <span v-if="element.pageStart" class="page-number">
                  第 {{ element.pageStart }}<template v-if="element.pageEnd && element.pageEnd !== element.pageStart">–{{ element.pageEnd }}</template> 页
                </span>
              </div>
              <mind-map-preview v-if="element.type === 'MINDMAP' && mindMapData(element)" :data="mindMapData(element)" />
              <flowchart-preview v-else-if="element.type === 'FLOWCHART' && flowchartData(element)" :data="flowchartData(element)" />
              <smart-art-preview v-else-if="element.type === 'SMARTART' && smartArtData(element)" :data="smartArtData(element)" />
              <ol v-else-if="element.type === 'TOC' && tocEntries(element).length" class="toc-list">
                <li
                  v-for="entry in tocEntries(element)"
                  :key="`${element.elementId}-${entry.index}`"
                  :style="{ paddingLeft: `${(entry.level - 1) * 18}px` }"
                >
                  <span class="toc-number">{{ entry.number }}</span>
                  <span class="toc-title" :title="entry.title">{{ entry.title }}</span>
                  <span v-if="entry.page" class="toc-page">{{ entry.page }}</span>
                </li>
              </ol>
              <div v-else-if="previewArtifact(element)" class="element-image-wrap">
                <div v-if="previewLoading[previewArtifact(element).artifactId]" class="element-image-state">
                  <i class="el-icon-loading" aria-hidden="true"></i>
                  <span>正在加载图片预览...</span>
                </div>
                <img
                  v-else-if="previewUrls[previewArtifact(element).artifactId]"
                  class="element-image-preview"
                  :src="previewUrls[previewArtifact(element).artifactId]"
                  :alt="element.title || previewArtifact(element).fileName || '提取图片'"
                  @error="handlePreviewError(previewArtifact(element))"
                />
                <div v-else class="element-image-state muted-content">
                  <i class="el-icon-picture-outline" aria-hidden="true"></i>
                  <span>图片预览暂不可用，可下载原始文件查看</span>
                </div>
                <p v-if="imageCaption(element)" class="element-image-caption">{{ imageCaption(element) }}</p>
              </div>
              <div v-else-if="element.contentText" class="element-content">{{ element.contentText }}</div>
              <div v-else class="element-content muted-content">已提取原始文件，可在下方下载查看</div>
              <div v-if="visibleArtifacts(element).length" class="artifact-list">
                <button
                  v-for="artifact in visibleArtifacts(element)"
                  :key="artifact.artifactId"
                  type="button"
                  :disabled="artifact.status !== 'READY'"
                  @click="downloadArtifact(artifact)"
                >
                  <i class="el-icon-download" aria-hidden="true"></i>
                  {{ artifact.fileName || '下载文件' }}
                </button>
              </div>
            </article>
            <el-pagination
              v-if="elementTotal > elementPageSize"
              class="result-pagination"
              background
              layout="prev, pager, next"
              :current-page="elementPage"
              :page-size="elementPageSize"
              :total="elementTotal"
              @current-change="changeElementPage"
            ></el-pagination>
          </div>
        </div>
      </main>

      <aside class="history-panel" aria-label="历史记录">
        <div class="history-scroll" @scroll="handleHistoryScroll">
          <div v-if="historyLoading && !history.length" class="history-loading">
            <i class="el-icon-loading" aria-hidden="true"></i>
            <span>正在载入</span>
          </div>
          <div v-else-if="!hasLogin" class="history-empty">
            <i class="el-icon-user" aria-hidden="true"></i>
            <span>登录后查看历史记录</span>
          </div>
          <div v-else-if="!history.length" class="history-empty">
            <i class="el-icon-document" aria-hidden="true"></i>
            <span>暂无解析记录</span>
          </div>
          <button
            v-for="task in history"
            :key="task.taskId"
            type="button"
            class="history-item"
            :class="{ active: currentTask && currentTask.taskId === task.taskId }"
            :aria-current="currentTask && currentTask.taskId === task.taskId ? 'true' : null"
            @click="openHistoryTask(task)"
          >
            <span class="history-thumbnail">
              <img :src="fileIcon(task.fileExtension)" alt="" />
              <span v-if="isActiveStatus(task.status)" class="thumbnail-progress">
                <span :style="{ width: `${Math.max(6, task.progress || 0)}%` }"></span>
              </span>
            </span>
            <span class="history-info">
              <strong :title="task.fileName">{{ task.fileName }}</strong>
              <span class="history-status" :class="statusClass(task.status)">{{ historySummary(task) }}</span>
              <time>{{ formatTime(task.createTime) }}</time>
            </span>
          </button>
          <div v-if="historyLoading && history.length" class="history-loading more">
            <i class="el-icon-loading" aria-hidden="true"></i>
          </div>
          <div v-else-if="history.length && history.length >= historyTotal" class="history-end">仅保留近 7 天记录</div>
        </div>
      </aside>
        </div>
      </div>
    </div>
    <site-footer class="material-extract-footer" />
  </div>
</template>

<script>
import WorkspaceExpandedNav from '@/components/WorkspaceExpandedNav.vue'
import SiteHeader from '@/components/home/SiteHeader.vue'
import SiteFooter from '@/components/home/SiteFooter.vue'
import MindMapPreview from '@/components/material/MindMapPreview.vue'
import FlowchartPreview from '@/components/material/FlowchartPreview.vue'
import SmartArtPreview from '@/components/material/SmartArtPreview.vue'

const ACTIVE_STATUSES = ['QUEUED', 'PARSING', 'NORMALIZING', 'GENERATING', 'PACKAGING', 'CANCEL_REQUESTED']
const TERMINAL_RESULT_STATUSES = ['SUCCEEDED', 'PARTIAL']
const FAILED_STATUSES = ['FAILED', 'CANCELED', 'EXPIRED']

export default {
  name: 'MaterialExtract',
  components: { WorkspaceExpandedNav, SiteHeader, SiteFooter, MindMapPreview, FlowchartPreview, SmartArtPreview },
  data () {
    return {
      authStateVersion: 0,
      dragActive: false,
      uploading: false,
      exporting: false,
      historyLoading: false,
      elementsLoading: false,
      currentTask: null,
      history: [],
      historyPage: 1,
      historyPageSize: 20,
      historyTotal: 0,
      elements: [],
      elementPage: 1,
      elementPageSize: 20,
      elementTotal: 0,
      selectedType: 'ALL',
      previewUrls: {},
      previewLoading: {},
      previewFailed: {},
      pollTimer: null,
      activeRequestId: 0,
      designPreview: process.env.NODE_ENV === 'development' && this.$route.query.preview === '1',
      elementTypes: [
        { type: 'TOC', label: '目录', icon: 'el-icon-s-unfold', iconClass: 'icon-toc' },
        { type: 'REFERENCE', label: '参考文献', icon: 'el-icon-reading', iconClass: 'icon-reference' },
        { type: 'TABLE', label: '表格', icon: 'el-icon-s-grid', iconClass: 'icon-table' },
        { type: 'RASTER_IMAGE', label: '图片(位图)', icon: 'el-icon-picture-outline', iconClass: 'icon-image' },
        { type: 'VECTOR_IMAGE', label: '矢量图', icon: 'el-icon-collection-tag', iconClass: 'icon-vector' },
        { type: 'CHART', label: '图表', icon: 'el-icon-pie-chart', iconClass: 'icon-chart' },
        { type: 'FORMULA', label: '公式', icon: 'el-icon-set-up', iconClass: 'icon-formula' },
        { type: 'FLOWCHART', label: '流程图', icon: 'el-icon-share', iconClass: 'icon-flowchart' },
        { type: 'SMARTART', label: 'SmartArt', icon: 'el-icon-data-analysis', iconClass: 'icon-smartart' },
        { type: 'MINDMAP', label: '思维导图', icon: 'el-icon-connection', iconClass: 'icon-mindmap' }
      ]
    }
  },
  computed: {
    hasLogin () {
      return this.designPreview || (this.authStateVersion >= 0 && Boolean(localStorage.getItem('token')))
    },
    canExport () {
      return this.currentTask && TERMINAL_RESULT_STATUSES.includes(this.currentTask.status) && (this.currentTask.totalElements || 0) > 0
    },
    canRetry () {
      return this.currentTask && ['FAILED', 'CANCELED'].includes(this.currentTask.status)
    },
    filters () {
      const counts = (this.currentTask && this.currentTask.elementCounts) || {}
      const allCount = Object.keys(counts).reduce((total, key) => total + Number(counts[key] || 0), 0)
      return [
        { type: 'ALL', label: '全部', count: allCount || (this.currentTask ? this.currentTask.totalElements || 0 : 0) },
        ...this.elementTypes.map(item => ({ ...item, count: Number(counts[item.type] || 0) }))
      ]
    }
  },
  created () {
    if (this.designPreview) {
      this.seedDesignPreview()
    } else if (this.hasLogin) {
      this.loadHistory(true)
    }
  },
  beforeDestroy () {
    this.stopPolling()
  },
  methods: {
    handleAuthUpdated () {
      this.authStateVersion += 1
      if (!this.designPreview && this.hasLogin) this.loadHistory(true, true)
    },
    apiData (response) {
      const body = response && response.data
      if (body && [401, 4011].includes(body.code)) {
        this.openLogin()
        const error = new Error(body.msg || '登录状态已失效，请重新登录')
        error.authRequired = true
        throw error
      }
      if (!body || body.code !== 200) throw new Error((body && body.msg) || '请求失败，请稍后重试')
      return body.data
    },
    requireLogin () {
      if (localStorage.getItem('token')) return true
      this.openLogin()
      return false
    },
    authenticatedRequest (config) {
      const token = localStorage.getItem('token')
      if (!token) {
        this.openLogin()
        const error = new Error('请先登录')
        error.authRequired = true
        return Promise.reject(error)
      }
      return this.$http({
        ...config,
        headers: {
          ...(config.headers || {}),
          Authorization: token
        }
      }).catch(error => {
        const response = error && error.response
        const body = response && response.data
        if ((response && [401, 403].includes(response.status)) || (body && [401, 4011].includes(body.code))) {
          this.openLogin()
          error.authRequired = true
        }
        return Promise.reject(error)
      })
    },
    chooseFile () {
      if (this.uploading) return
      if (!this.requireLogin()) return
      this.$refs.fileInput.click()
    },
    handleFileChange (event) {
      const file = event.target.files && event.target.files[0]
      if (file) this.submitFile(file)
      event.target.value = ''
    },
    handleDrop (event) {
      this.dragActive = false
      if (!this.requireLogin()) return
      const file = event.dataTransfer.files && event.dataTransfer.files[0]
      if (file) this.submitFile(file)
    },
    validateFile (file) {
      const extension = String(file.name || '').split('.').pop().toLowerCase()
      if (!['doc', 'docx', 'pdf'].includes(extension)) return '仅支持 .docx、.doc、.pdf 文档'
      if (file.size > 100 * 1024 * 1024) return '文件大小不能超过 100MB'
      if (!file.size) return '不能上传空文件'
      return ''
    },
    async submitFile (file) {
      const error = this.validateFile(file)
      if (error) {
        this.$message.warning(error)
        return
      }
      if (this.designPreview) {
        this.addPreviewUpload(file)
        return
      }
      const form = new FormData()
      form.append('file', file)
      this.uploading = true
      try {
        const task = this.apiData(await this.authenticatedRequest({
          method: 'post',
          url: '/chatApi/v1/material-extract/tasks',
          data: form,
          headers: { 'Content-Type': 'multipart/form-data' }
        }))
        this.upsertHistory(task, true)
        await this.applyTask(task, { loadElements: false })
        this.startPolling()
        this.$message.success('文档已上传，正在提取素材')
      } catch (e) {
        this.handleRequestError(e, '文档上传失败')
      } finally {
        this.uploading = false
      }
    },
    async loadHistory (reset = false, openLatest = false) {
      if (this.historyLoading || !this.hasLogin) return
      if (reset) this.historyPage = 1
      if (!reset && this.history.length >= this.historyTotal) return
      this.historyLoading = true
      try {
        const data = this.apiData(await this.authenticatedRequest({
          method: 'get',
          url: '/chatApi/v1/material-extract/tasks',
          params: { current: this.historyPage, size: this.historyPageSize }
        })) || {}
        const records = data.records || []
        this.history = reset ? records : this.history.concat(records.filter(item => !this.history.some(existing => existing.taskId === item.taskId)))
        this.historyTotal = Number(data.total || this.history.length)
        if (openLatest && !this.currentTask && this.history.length) await this.openHistoryTask(this.history[0])
      } catch (e) {
        this.handleRequestError(e, '历史记录加载失败', false)
      } finally {
        this.historyLoading = false
      }
    },
    handleHistoryScroll (event) {
      const target = event.target
      if (target.scrollTop + target.clientHeight < target.scrollHeight - 48) return
      if (this.historyLoading || this.history.length >= this.historyTotal) return
      this.historyPage += 1
      this.loadHistory(false)
    },
    async openHistoryTask (task) {
      this.stopPolling()
      const requestId = ++this.activeRequestId
      this.currentTask = { ...task }
      this.selectedType = 'ALL'
      this.elements = []
      this.resetPreviewState()
      this.elementTotal = 0
      this.elementPage = 1
      if (this.designPreview) {
        this.applyPreviewTask(task)
        return
      }
      try {
        const detail = this.apiData(await this.authenticatedRequest({
          method: 'get',
          url: `/chatApi/v1/material-extract/tasks/${task.taskId}`
        }))
        if (requestId !== this.activeRequestId) return
        await this.applyTask(detail, { loadElements: true })
      } catch (e) {
        if (requestId === this.activeRequestId) this.handleRequestError(e, '记录恢复失败')
      }
    },
    async applyTask (task, options = {}) {
      this.currentTask = { ...task }
      this.upsertHistory(task)
      if (options.loadElements !== false && TERMINAL_RESULT_STATUSES.includes(task.status)) await this.loadElements()
      if (this.isActiveStatus(task.status)) this.startPolling()
    },
    upsertHistory (task, prepend = false) {
      const index = this.history.findIndex(item => item.taskId === task.taskId)
      const summary = { ...(index >= 0 ? this.history[index] : {}), ...task }
      if (index >= 0) this.history.splice(index, 1, summary)
      else if (prepend) {
        this.history.unshift(summary)
        this.historyTotal += 1
      }
    },
    async loadElements () {
      if (!this.currentTask || !TERMINAL_RESULT_STATUSES.includes(this.currentTask.status)) return
      const taskId = this.currentTask.taskId
      const requestId = this.activeRequestId
      this.elementsLoading = true
      try {
        const data = this.apiData(await this.authenticatedRequest({
          method: 'get',
          url: `/chatApi/v1/material-extract/tasks/${taskId}/elements`,
          params: { current: this.elementPage, size: this.elementPageSize, type: this.selectedType }
        })) || {}
        if (requestId !== this.activeRequestId || !this.currentTask || this.currentTask.taskId !== taskId) return
        this.elements = data.records || []
        this.elementTotal = Number(data.total || 0)
        this.loadElementPreviews(this.elements, taskId, requestId)
      } catch (e) {
        this.handleRequestError(e, '提取结果加载失败', false)
      } finally {
        if (requestId === this.activeRequestId) this.elementsLoading = false
      }
    },
    selectType (type) {
      if (this.selectedType === type) return
      this.selectedType = type
      this.elementPage = 1
      if (this.designPreview) this.applyPreviewElements()
      else this.loadElements()
    },
    changeElementPage (page) {
      this.elementPage = page
      this.loadElements()
    },
    startPolling () {
      this.stopPolling()
      if (!this.currentTask || !this.isActiveStatus(this.currentTask.status) || this.designPreview) return
      this.pollTimer = window.setInterval(this.refreshCurrentTask, 2500)
    },
    stopPolling () {
      if (this.pollTimer) window.clearInterval(this.pollTimer)
      this.pollTimer = null
    },
    async refreshCurrentTask () {
      if (!this.currentTask) return
      const taskId = this.currentTask.taskId
      try {
        const task = this.apiData(await this.authenticatedRequest({
          method: 'get',
          url: `/chatApi/v1/material-extract/tasks/${taskId}`
        }))
        if (!this.currentTask || this.currentTask.taskId !== taskId) return
        const wasActive = this.isActiveStatus(this.currentTask.status)
        this.currentTask = task
        this.upsertHistory(task)
        if (wasActive && !this.isActiveStatus(task.status)) {
          this.stopPolling()
          if (TERMINAL_RESULT_STATUSES.includes(task.status)) await this.loadElements()
        }
      } catch (e) {
        this.stopPolling()
      }
    },
    async cancelTask () {
      if (!this.currentTask || this.designPreview) return
      try {
        const task = this.apiData(await this.authenticatedRequest({
          method: 'post',
          url: `/chatApi/v1/material-extract/tasks/${this.currentTask.taskId}/cancel`
        }))
        await this.applyTask(task, { loadElements: false })
      } catch (e) {
        this.handleRequestError(e, '取消任务失败')
      }
    },
    async retryTask () {
      if (!this.currentTask) return
      if (this.designPreview) {
        this.currentTask.status = 'PARSING'
        this.currentTask.progress = 36
        this.currentTask.progressMessage = '正在解析文档内容'
        this.upsertHistory(this.currentTask)
        return
      }
      try {
        const task = this.apiData(await this.authenticatedRequest({
          method: 'post',
          url: `/chatApi/v1/material-extract/tasks/${this.currentTask.taskId}/retry`
        }))
        this.elements = []
        this.resetPreviewState()
        await this.applyTask(task, { loadElements: false })
      } catch (e) {
        this.handleRequestError(e, '重新提取失败')
      }
    },
    async downloadArtifact (artifact) {
      if (this.designPreview) {
        this.$message.success(`已准备下载：${artifact.fileName}`)
        return
      }
      try {
        const data = this.apiData(await this.authenticatedRequest({
          method: 'get',
          url: `/chatApi/v1/material-extract/artifacts/${artifact.artifactId}/download-url`
        }))
        window.open(data.url, '_blank', 'noopener')
      } catch (e) {
        this.handleRequestError(e, '下载地址生成失败')
      }
    },
    visibleArtifacts (element) {
      return ((element && element.artifacts) || []).filter(artifact => !['RAW', 'PREVIEW'].includes(artifact.kind))
    },
    referenceTitle (element) {
      const data = this.parseContentJson(element) || {}
      return data.referenceTitle || element.title || element.contentText || '未识别题名'
    },
    openReferenceSearch (provider, element) {
      const title = this.referenceTitle(element)
      if (!title || title === '未识别题名') {
        this.$message.warning('未识别到参考文献题名')
        return
      }
      const query = encodeURIComponent(title)
      let url = ''
      if (provider === 'wanfang') url = `https://s.wanfangdata.com.cn/paper?q=${query}`
      else if (provider === 'cnki') url = `https://kns.cnki.net/kns8/defaultresult/index?korder=TI&kw=${query}`
      else url = this.$router.resolve({ path: '/scholar', query: { q: title, searchMode: 'exact' } }).href
      const opened = window.open(url, '_blank')
      if (opened) opened.opener = null
    },
    async exportAll () {
      if (!this.canExport || this.exporting) return
      if (this.designPreview) {
        this.$message.success('批量导出文件已生成')
        return
      }
      this.exporting = true
      try {
        const artifact = this.apiData(await this.authenticatedRequest({
          method: 'post',
          url: `/chatApi/v1/material-extract/tasks/${this.currentTask.taskId}/export`
        }))
        if (artifact.status !== 'READY') await this.waitForExport()
        const data = this.apiData(await this.authenticatedRequest({
          method: 'get',
          url: `/chatApi/v1/material-extract/tasks/${this.currentTask.taskId}/export-url`
        }))
        window.open(data.url, '_blank', 'noopener')
      } catch (e) {
        this.handleRequestError(e, '批量导出失败')
      } finally {
        this.exporting = false
      }
    },
    async waitForExport () {
      for (let index = 0; index < 20; index += 1) {
        await new Promise(resolve => window.setTimeout(resolve, 1000))
        try {
          const response = await this.authenticatedRequest({
            method: 'get',
            url: `/chatApi/v1/material-extract/tasks/${this.currentTask.taskId}/export-url`
          })
          this.apiData(response)
          return
        } catch (e) {
          if (e && e.authRequired) throw e
        }
      }
      throw new Error('导出文件生成超时，请稍后重试')
    },
    openLogin () {
      if (this.$authPopup) this.$authPopup.open(process.env.VUE_APP_WEB_LOGIN_URL || '/login')
      else this.$router.push('/login')
    },
    handleRequestError (error, fallback, notify = true) {
      if (error && error.authRequired) return
      const response = error && error.response && error.response.data
      const message = (response && response.msg) || error.message || fallback
      if (response && [401, 4011].includes(response.code)) {
        this.openLogin()
        return
      }
      if (notify) this.$message.error(message || fallback)
    },
    fileIcon (extension) {
      return String(extension || '').toLowerCase() === 'pdf'
        ? require('@/assets/pdf.png')
        : require('@/assets/docx.png')
    },
    elementTypeMeta (type) {
      return this.elementTypes.find(item => item.type === type) || { label: '素材', icon: 'el-icon-document' }
    },
    parseContentJson (element) {
      const value = element && element.contentJson
      if (!value) return null
      if (typeof value === 'object') return value
      try {
        return JSON.parse(value)
      } catch (e) {
        return null
      }
    },
    mindMapData (element) {
      const content = this.parseContentJson(element)
      return content && Array.isArray(content.nodes) && content.nodes.length ? content : null
    },
    flowchartData (element) {
      const content = this.parseContentJson(element)
      if (!content) return null
      const hasNodes = Array.isArray(content.nodes) && content.nodes.length
      const hasOfficeShapes = Array.isArray(content.shapes) && content.shapes.length
      return hasNodes || hasOfficeShapes ? content : null
    },
    smartArtData (element) {
      const content = this.parseContentJson(element)
      return content && Array.isArray(content.nodes) && content.nodes.length ? content : null
    },
    tocEntries (element) {
      const content = this.parseContentJson(element)
      let source = content && Array.isArray(content.entries) ? content.entries : []
      if (!source.length) {
        source = String((element && element.contentText) || '')
          .split(/\r?\n/)
          .filter(line => line.trim())
          .map(line => ({
            level: Math.floor((line.match(/^\s*/) || [''])[0].replace(/\t/g, '  ').length / 2) + 1,
            title: line.trim(),
            page: null
          }))
      }
      if (!source.length) return []
      const rawLevels = source.map(item => Math.max(1, Number(item && item.level) || 1))
      const minLevel = Math.min(...rawLevels)
      const counters = []
      return source.map((item, index) => {
        const level = Math.min(6, Math.max(1, rawLevels[index] - minLevel + 1))
        counters.length = level
        for (let depth = 0; depth < level - 1; depth += 1) {
          if (!counters[depth]) counters[depth] = 1
        }
        counters[level - 1] = (counters[level - 1] || 0) + 1
        return {
          index,
          level,
          number: counters.join('.'),
          title: String((item && (item.title || item.text || item.content)) || `目录项 ${index + 1}`).trim(),
          page: item && item.page ? item.page : null
        }
      })
    },
    previewArtifact (element) {
      if (!element) return null
      const browserImageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg']
      return (element.artifacts || []).find(artifact => {
        if (!artifact || artifact.status !== 'READY') return false
        const contentType = String(artifact.contentType || '').toLowerCase()
        const extension = String(artifact.fileExtension || (artifact.fileName || '').split('.').pop() || '').toLowerCase()
        return contentType.startsWith('image/') && browserImageExtensions.includes(extension)
      }) || null
    },
    imageCaption (element) {
      const text = String((element && element.contentText) || '').trim()
      if (!text || /<\/?(?:table|thead|tbody|tr|td|th)\b/i.test(text)) return ''
      return text
    },
    loadElementPreviews (elements, taskId, requestId) {
      (elements || []).forEach(element => this.loadElementPreview(element, taskId, requestId))
    },
    async loadElementPreview (element, taskId, requestId) {
      const artifact = this.previewArtifact(element)
      if (!artifact || this.previewUrls[artifact.artifactId] || this.previewLoading[artifact.artifactId] || this.previewFailed[artifact.artifactId]) return
      this.$set(this.previewLoading, artifact.artifactId, true)
      try {
        const data = this.apiData(await this.authenticatedRequest({
          method: 'get',
          url: `/chatApi/v1/material-extract/artifacts/${artifact.artifactId}/download-url`
        }))
        if (requestId !== this.activeRequestId || !this.currentTask || this.currentTask.taskId !== taskId) return
        this.$set(this.previewUrls, artifact.artifactId, data.url)
      } catch (e) {
        if (requestId === this.activeRequestId) this.$set(this.previewFailed, artifact.artifactId, true)
      } finally {
        this.$delete(this.previewLoading, artifact.artifactId)
      }
    },
    handlePreviewError (artifact) {
      if (!artifact) return
      this.$delete(this.previewUrls, artifact.artifactId)
      this.$set(this.previewFailed, artifact.artifactId, true)
    },
    resetPreviewState () {
      this.previewUrls = {}
      this.previewLoading = {}
      this.previewFailed = {}
    },
    isActiveStatus (status) {
      return ACTIVE_STATUSES.includes(status)
    },
    isFailedStatus (status) {
      return FAILED_STATUSES.includes(status)
    },
    statusText (status) {
      return {
        QUEUED: '等待处理',
        PARSING: '正在解析',
        NORMALIZING: '正在整理',
        GENERATING: '正在生成',
        PACKAGING: '正在打包',
        SUCCEEDED: '提取完成',
        PARTIAL: '部分完成',
        FAILED: '提取失败',
        CANCEL_REQUESTED: '正在取消',
        CANCELED: '已取消',
        EXPIRED: '已过期'
      }[status] || '处理中'
    },
    statusClass (status) {
      if (TERMINAL_RESULT_STATUSES.includes(status)) return 'success'
      if (FAILED_STATUSES.includes(status)) return 'failed'
      return 'processing'
    },
    historySummary (task) {
      if (this.isActiveStatus(task.status)) return `${this.statusText(task.status)} ${task.progress || 0}%`
      if (task.status === 'SUCCEEDED') return `已提取 ${task.totalElements || 0} 项素材`
      if (task.status === 'PARTIAL') return `已提取 ${task.successElements || 0} 项，${task.failedElements || 0} 项失败`
      return task.errorMessage || this.statusText(task.status)
    },
    formatFileSize (size) {
      const bytes = Number(size || 0)
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`
    },
    formatTime (value) {
      if (!value) return '-'
      const date = new Date(String(value).replace(' ', 'T'))
      if (Number.isNaN(date.getTime())) return String(value)
      const pad = number => String(number).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    },
    seedDesignPreview () {
      const now = Date.now()
      this.history = [
        this.previewTask('preview-1', '基于用量下的城市公共交通研究.docx', 'docx', 'SUCCEEDED', 26, now - 18 * 60 * 1000),
        this.previewTask('preview-2', 'Renewing urban infrastructure.pdf', 'pdf', 'PARSING', 0, now - 42 * 60 * 1000, 64),
        this.previewTask('preview-3', '万方与社科文献数据说明.docx', 'docx', 'PARTIAL', 18, now - 2 * 60 * 60 * 1000)
      ]
      this.historyTotal = this.history.length
    },
    previewTask (taskId, fileName, fileExtension, status, totalElements, timestamp, progress = 100) {
      return {
        taskId,
fileName,
fileExtension,
fileSize: fileExtension === 'pdf' ? 4832000 : 2198000,
status,
progress,
        progressMessage: this.statusText(status),
totalElements,
        successElements: status === 'PARTIAL' ? totalElements - 2 : totalElements,
        failedElements: status === 'PARTIAL' ? 2 : 0,
        createTime: new Date(timestamp).toISOString(),
        elementCounts: { TOC: 1, REFERENCE: 7, TABLE: 5, RASTER_IMAGE: 4, VECTOR_IMAGE: 1, CHART: 3, FORMULA: 2, FLOWCHART: 1, SMARTART: 1, MINDMAP: 1 }
      }
    },
    applyPreviewTask (task) {
      this.currentTask = { ...task }
      this.applyPreviewElements()
    },
    applyPreviewElements () {
      const vectorArtifactId = 'preview-vector-svg'
      this.$set(this.previewUrls, vectorArtifactId, require('@/assets/material-vector-sample.svg'))
      const flowchartData = {
        title: '文档素材提取流程',
        nodes: [
          { id: 'start', type: 'start', label: '开始', x: 330, y: 65, width: 130, height: 62 },
          { id: 'upload', type: 'process', label: '上传 Word / PDF', x: 330, y: 160, width: 170, height: 68 },
          { id: 'validate', type: 'decision', label: '文件校验通过？', x: 330, y: 270, width: 170, height: 100 },
          { id: 'extract', type: 'process', label: '解析并提取素材', x: 510, y: 390, width: 170, height: 68 },
          { id: 'success', type: 'end', label: '生成提取结果', x: 510, y: 510, width: 150, height: 62 },
          { id: 'error', type: 'process', label: '提示文件错误', x: 150, y: 390, width: 170, height: 68 },
          { id: 'failed', type: 'end', label: '结束', x: 150, y: 510, width: 130, height: 62 }
        ],
        edges: [
          { from: 'start', to: 'upload' },
          { from: 'upload', to: 'validate' },
          { from: 'validate', to: 'extract', label: '是' },
          { from: 'extract', to: 'success' },
          { from: 'validate', to: 'error', label: '否' },
          { from: 'error', to: 'failed' }
        ]
      }
      const smartArtData = {
        title: '城市公共交通优化策略',
        layout: 'hierarchy',
        nodes: [
          { id: 'strategy', type: 'doc', text: '城市公共交通优化' },
          { id: 'supply', type: 'node', text: '供给侧优化' },
          { id: 'service', type: 'node', text: '服务体验提升' },
          { id: 'digital', type: 'node', text: '数字化治理' },
          { id: 'network', type: 'node', text: '线路与班次重构' },
          { id: 'transfer', type: 'node', text: '枢纽换乘衔接' },
          { id: 'realtime', type: 'node', text: '实时信息服务' }
        ],
        edges: [
          { sourceId: 'strategy', targetId: 'supply', type: 'parOf' },
          { sourceId: 'strategy', targetId: 'service', type: 'parOf' },
          { sourceId: 'strategy', targetId: 'digital', type: 'parOf' },
          { sourceId: 'supply', targetId: 'network', type: 'parOf' },
          { sourceId: 'service', targetId: 'transfer', type: 'parOf' },
          { sourceId: 'digital', targetId: 'realtime', type: 'parOf' }
        ],
        sourcePart: 'word/diagrams/data1.xml'
      }
      const records = [
        { elementId: 'e1', type: 'TOC', title: '目录-1', pageStart: 2, contentText: '1 研究背景\n2 城市交通用量特征\n3 数据分析与结论', status: 'SUCCEEDED', artifacts: [] },
        { elementId: 'ref-1', type: 'REFERENCE', subtype: 'paragraph', title: '[1] 张明, 李华. 城市公共交通客流特征与出行行为研究[J]. 城市交通, 2024, 22(3): 18-27.', pageStart: 28, pageEnd: 28, contentText: '[1] 张明, 李华. 城市公共交通客流特征与出行行为研究[J]. 城市交通, 2024, 22(3): 18-27.', contentJson: '{"style":"Reference","headingLevel":null}', fidelity: 'STRUCTURED', status: 'SUCCEEDED', artifacts: [] },
        { elementId: 'ref-2', type: 'REFERENCE', subtype: 'paragraph', title: '[2] 王磊. 公共交通网络优化方法与实践[M]. 北京: 人民交通出版社, 2023.', pageStart: 28, pageEnd: 28, contentText: '[2] 王磊. 公共交通网络优化方法与实践[M]. 北京: 人民交通出版社, 2023.', contentJson: '{"style":"Reference","headingLevel":null}', fidelity: 'STRUCTURED', status: 'SUCCEEDED', artifacts: [] },
        { elementId: 'ref-3', type: 'REFERENCE', subtype: 'paragraph', title: '[3] 陈宇, 赵宁. 基于多源数据的轨道交通客流预测模型[J]. 交通信息与安全, 2022, 40(6): 92-101.', pageStart: 28, pageEnd: 28, contentText: '[3] 陈宇, 赵宁. 基于多源数据的轨道交通客流预测模型[J]. 交通信息与安全, 2022, 40(6): 92-101.', contentJson: '{"style":"Reference","headingLevel":null}', fidelity: 'STRUCTURED', status: 'SUCCEEDED', artifacts: [] },
        { elementId: 'ref-4', type: 'REFERENCE', subtype: 'paragraph', title: '[4] LIU Y, WANG J. Travel demand analysis for urban transit systems[J]. Transportation Research, 2021, 48(2): 115-129.', pageStart: 29, pageEnd: 29, contentText: '[4] LIU Y, WANG J. Travel demand analysis for urban transit systems[J]. Transportation Research, 2021, 48(2): 115-129.', contentJson: '{"style":"Reference","headingLevel":null}', fidelity: 'STRUCTURED', status: 'SUCCEEDED', artifacts: [] },
        { elementId: 'ref-5', type: 'REFERENCE', subtype: 'paragraph', title: '[5] World Bank. Urban Transport and Sustainable Mobility[R]. Washington, DC: World Bank, 2022.', pageStart: 29, pageEnd: 29, contentText: '[5] World Bank. Urban Transport and Sustainable Mobility[R]. Washington, DC: World Bank, 2022.', contentJson: '{"style":"Reference","headingLevel":null}', fidelity: 'STRUCTURED', status: 'SUCCEEDED', artifacts: [] },
        { elementId: 'ref-6', type: 'REFERENCE', subtype: 'paragraph', title: '[6] 交通运输部. 2023年城市轨道交通运营数据统计和分析报告[R]. 北京, 2024.', pageStart: 29, pageEnd: 29, contentText: '[6] 交通运输部. 2023年城市轨道交通运营数据统计和分析报告[R]. 北京, 2024.', contentJson: '{"style":"Reference","headingLevel":null}', fidelity: 'STRUCTURED', status: 'SUCCEEDED', artifacts: [] },
        { elementId: 'ref-7', type: 'REFERENCE', subtype: 'paragraph', title: '[7] 周凯. 城市公交服务水平评价指标体系研究[D]. 上海: 同济大学, 2022.', pageStart: 29, pageEnd: 29, contentText: '[7] 周凯. 城市公交服务水平评价指标体系研究[D]. 上海: 同济大学, 2022.', contentJson: '{"style":"Reference","headingLevel":null}', fidelity: 'STRUCTURED', status: 'SUCCEEDED', artifacts: [] },
        { elementId: 'e2', type: 'TABLE', title: '表格-1', pageStart: 8, contentText: '表 2 主要城市公共交通客运量与同比变化数据', status: 'SUCCEEDED', artifacts: [{ artifactId: 'a2', fileName: '表格-1.xlsx', status: 'READY' }] },
        { elementId: 'e3', type: 'RASTER_IMAGE', title: '图片-1', pageStart: 11, contentText: '城市轨道交通客流变化趋势图', status: 'SUCCEEDED', artifacts: [{ artifactId: 'a3', fileName: '图片-1.png', status: 'READY' }] },
        { elementId: 'vector-1', type: 'VECTOR_IMAGE', subtype: 'svg', title: '城市公共交通网络示意图', pageStart: 14, pageEnd: 14, contentText: 'SVG 矢量线路图，可在页面内无损缩放预览，也可下载原始文件。', contentJson: '{"format":"svg","width":960,"height":500}', fidelity: 'ORIGINAL', status: 'SUCCEEDED', artifacts: [{ artifactId: vectorArtifactId, kind: 'PRIMARY', fileName: '城市公共交通网络示意图.svg', fileExtension: 'svg', contentType: 'image/svg+xml', status: 'READY' }] },
        { elementId: 'e4', type: 'CHART', title: '图表-1', pageStart: 15, contentText: '不同出行方式占比分析', status: 'SUCCEEDED', artifacts: [{ artifactId: 'a4', fileName: '图表-1.png', status: 'READY' }] },
        { elementId: 'flowchart-1', type: 'FLOWCHART', subtype: 'office-shapes', title: '文档素材提取流程', pageStart: 18, pageEnd: 18, contentText: '开始；上传 Word / PDF；文件校验；解析并提取素材；生成提取结果', contentJson: flowchartData, fidelity: 'STRUCTURED', status: 'SUCCEEDED', artifacts: [{ artifactId: 'preview-flowchart-json', kind: 'PRIMARY', fileName: '文档素材提取流程.json', fileExtension: 'json', contentType: 'application/json', status: 'READY' }] },
        { elementId: 'smartart-1', type: 'SMARTART', subtype: 'office-smartart', title: '城市公共交通优化策略', pageStart: 20, pageEnd: 20, contentText: '城市公共交通优化；供给侧优化；服务体验提升；数字化治理；线路与班次重构；枢纽换乘衔接；实时信息服务', contentJson: smartArtData, fidelity: 'STRUCTURED', status: 'SUCCEEDED', artifacts: [{ artifactId: 'preview-smartart-json', kind: 'PRIMARY', fileName: '城市公共交通优化策略.json', fileExtension: 'json', contentType: 'application/json', status: 'READY' }] },
        { elementId: 'e5', type: 'MINDMAP', subtype: 'office-smartart-mindmap', title: '文件内思维导图-1', pageStart: 21, pageEnd: 21, contentText: '城市公共交通研究；用量变化；影响因素；优化建议', contentJson: { title: '城市公共交通研究思维导图', sourceKind: 'office-smartart', sourcePart: 'word/diagrams/data2.xml', nodes: [{ id: 'm-root', parentId: null, title: '城市公共交通研究思维导图', summary: '' }, { id: 'm-usage', parentId: 'm-root', title: '用量变化', summary: '' }, { id: 'm-factor', parentId: 'm-root', title: '影响因素', summary: '' }, { id: 'm-advice', parentId: 'm-root', title: '优化建议', summary: '' }] }, fidelity: 'STRUCTURED', status: 'SUCCEEDED', artifacts: [{ artifactId: 'a5', kind: 'PRIMARY', fileName: '文件内思维导图-1.json', fileExtension: 'json', contentType: 'application/json', status: 'READY' }] }
      ]
      this.elements = this.selectedType === 'ALL' ? records : records.filter(item => item.type === this.selectedType)
      this.elementTotal = this.elements.length
    },
    addPreviewUpload (file) {
      const extension = String(file.name).split('.').pop().toLowerCase()
      const task = this.previewTask(`preview-${Date.now()}`, file.name, extension, 'PARSING', 0, Date.now(), 18)
      task.fileSize = file.size
      task.progressMessage = '正在解析文档内容'
      this.history.unshift(task)
      this.historyTotal += 1
      this.applyPreviewTask(task)
    }
  }
}
</script>

<style scoped>
.material-extract-page {
  --accent: #17b77b;
  --accent-dark: #0c9863;
  --text: #202833;
  --muted: #788392;
  --line: #e3e8ee;
  height: 100vh;
  min-height: 680px;
  display: flex;
  flex-direction: column;
  isolation: isolate;
  overflow: hidden;
  background: #f5f7fa;
  color: var(--text);
  font-family: "Microsoft YaHei", "PingFang SC", sans-serif;
}

.extract-header {
  position: relative;
  z-index: 2;
  width: auto;
  height: 70px;
  flex: 0 0 70px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px 0 30px;
  background: #fff;
  border-bottom: 1px solid var(--line);
  box-sizing: border-box;
}

.extract-brand { display: flex; align-items: center; min-width: 0; gap: 10px; }
.extract-brand > i { color: var(--accent); font-size: 20px; }
.extract-brand strong { flex: none; font-size: 16px; }
.extract-brand > span:last-child { color: #6f7987; font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.header-divider { width: 1px; height: 18px; margin: 0 5px; background: #dfe4ea; }
.export-button { border-color: #dbe3ea; background: #edf5fa; color: #344150; font-weight: 600; }
.export-button:not(.is-disabled):hover { border-color: var(--accent); color: var(--accent-dark); background: #effaf6; }

.material-extract-body {
  position: relative;
  z-index: 1;
  min-height: 0;
  flex: 1 1 auto;
  display: flex;
  min-width: 0;
  overflow: hidden;
}

.material-extract-nav { position: relative; z-index: 1; flex: 0 0 200px; }
.material-extract-footer { flex: 0 0 64px; min-height: 64px; }

.material-extract-content {
  min-width: 0;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.extract-workspace {
  min-height: 0;
  min-width: 0;
  flex: 1 1 auto;
  display: grid;
  grid-template-columns: 312px minmax(420px, 1fr) 260px;
  overflow: hidden;
  background: #f5f7fa;
}

.upload-panel, .result-panel, .history-panel { min-width: 0; min-height: 0; box-sizing: border-box; }
.upload-panel { padding: 26px 20px 32px; overflow-y: auto; scrollbar-width: none; background: #fff; border-right: 1px solid var(--line); }
.upload-panel::-webkit-scrollbar { width: 0; height: 0; }
.upload-panel h2, .result-heading h2, .history-heading h2 { margin: 0; font-size: 15px; line-height: 22px; }

.upload-dropzone {
  position: relative;
  height: 188px;
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px dashed #cfd8e2;
  border-radius: 16px;
  background: #f7f9fb;
  cursor: pointer;
  transition: border-color .16s ease, background .16s ease;
  outline: none;
  overflow: hidden;
}
.upload-dropzone:hover, .upload-dropzone:focus, .upload-dropzone.dragging { border-color: var(--accent); background: #f2fbf7; }
.upload-dropzone.occupied { height: 164px; }
.file-input { display: none; }
.upload-document-icon { color: #6ad2ac; font-size: 35px; }
.upload-arrow-icon { margin-top: 8px; color: var(--accent); font-size: 20px; }
.upload-title { margin-top: 10px; font-size: 14px; font-weight: 700; }
.upload-tip { margin-top: 10px; color: #7b8491; font-size: 12px; line-height: 18px; text-align: center; }
.current-file-icon { width: 43px; height: 51px; object-fit: contain; }
.current-file-name { width: calc(100% - 32px); margin-top: 10px; overflow: hidden; color: #26323d; font-size: 13px; font-weight: 700; text-align: center; text-overflow: ellipsis; white-space: nowrap; }
.current-file-meta, .replace-file { margin-top: 6px; color: #8993a0; font-size: 11px; }
.replace-file { color: var(--accent-dark); }
.upload-mask { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; background: rgba(247, 249, 251, .94); color: var(--accent-dark); font-size: 13px; }
.upload-mask i { font-size: 26px; }
.supported-card, .overview-card { margin-top: 18px; padding: 16px; border: 1px solid #d8e1e9; border-radius: 16px; background: #eaf4ff; }
.supported-card h3, .overview-card h3 { margin: 0 0 10px; color: #657181; font-size: 12px; font-weight: 700; }
.supported-card ul { margin: 0; padding: 0; list-style: none; }
.supported-card li { height: 21px; display: flex; align-items: center; gap: 9px; font-size: 12px; }
.supported-card li i { width: 14px; font-size: 13px; text-align: center; }
.icon-toc { color: #6b63ff; }.icon-reference { color: #1976d2; }.icon-table { color: #009b69; }
.icon-image { color: #ff7f32; }.icon-vector { color: #7b35de; }.icon-chart { color: #ff3c82; }
.icon-formula { color: #f26322; }.icon-flowchart { color: #0a8fbd; }.icon-smartart { color: #4d42d9; }.icon-mindmap { color: #0aaa70; }

.overview-card { background: #fff; }
.overview-card.muted { opacity: .5; }
.overview-total { color: #52cfa2; font-size: 30px; font-weight: 700; line-height: 38px; }
.overview-label { color: #8a94a0; font-size: 11px; }
.overview-grid { margin-top: 14px; display: grid; grid-template-columns: 1fr 1fr; border-top: 1px solid #edf0f3; }
.overview-grid div { display: flex; flex-direction: column; gap: 3px; padding-top: 11px; }
.overview-grid strong { font-size: 15px; }.overview-grid span { color: #8a94a0; font-size: 11px; }

.result-panel { display: flex; flex-direction: column; overflow: hidden; background: #f6f8fb; }
.result-heading { height: 50px; flex: none; display: flex; align-items: center; justify-content: space-between; padding: 0 20px; background: #fff; border-bottom: 1px solid var(--line); }
.result-heading > div:first-child { display: flex; align-items: center; gap: 12px; min-width: 0; }
.active-file { max-width: 360px; overflow: hidden; color: #8a94a0; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.task-actions .el-button { color: var(--accent-dark); }
.filter-bar { min-height: 48px; flex: none; display: flex; align-items: center; gap: 7px; padding: 7px 18px; overflow-x: auto; scrollbar-width: thin; scrollbar-color: rgba(16, 185, 129, .24) transparent; background: #fff; border-bottom: 1px solid var(--line); box-sizing: border-box; }
.filter-bar::-webkit-scrollbar { height: 5px; }
.filter-bar::-webkit-scrollbar-track { background: transparent; }
.filter-bar::-webkit-scrollbar-thumb { border-radius: 999px; background: rgba(16, 185, 129, .24); }
.filter-bar button { flex: none; height: 27px; padding: 0 11px; border: 1px solid transparent; border-radius: 14px; background: transparent; color: #687382; font: inherit; font-size: 12px; cursor: pointer; }
.filter-bar button span { margin-left: 5px; opacity: .65; }
.filter-bar button:hover { color: var(--accent-dark); background: #eff9f5; }
.filter-bar button.active { background: var(--accent); color: #fff; }

.result-body { min-height: 0; flex: 1; overflow-y: auto; overscroll-behavior: contain; scrollbar-gutter: stable; scrollbar-width: thin; scrollbar-color: rgba(16, 185, 129, .24) transparent; padding: 22px; box-sizing: border-box; }
.result-body::-webkit-scrollbar { width: 5px; }
.result-body::-webkit-scrollbar-track { background: transparent; }
.result-body::-webkit-scrollbar-thumb { border-radius: 999px; background: rgba(16, 185, 129, .24); }
.empty-state, .progress-state, .result-loading { min-height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; color: #a5adb8; text-align: center; }
.empty-state > i { color: #e2e7ec; font-size: 49px; }
.empty-state h3, .progress-state h3 { margin: 16px 0 4px; color: #838c98; font-size: 17px; }
.empty-state p, .progress-state p { margin: 0; color: #9aa3ae; font-size: 13px; }
.failed-state > i { color: #e7a23c; }.failed-state .el-button { margin-top: 18px; background: var(--accent); border-color: var(--accent); }
.compact-empty { min-height: 360px; }
.progress-state { max-width: 430px; min-height: 0; height: 100%; margin: auto; }
.progress-icon { width: 62px; height: 62px; display: grid; place-items: center; border-radius: 50%; background: #e7f7f1; color: var(--accent); font-size: 28px; }
.progress-state .el-progress { width: 320px; margin-top: 22px; }
.result-loading { min-height: 300px; gap: 12px; font-size: 13px; }.result-loading i { color: var(--accent); font-size: 24px; }
.element-list { width: min(880px, 100%); margin: 0 auto; }
.element-card { margin-bottom: 12px; padding: 17px 18px; border: 1px solid #e1e7ed; border-radius: 10px; background: #fff; box-shadow: 0 2px 10px rgba(41, 59, 77, .035); }
.element-card-head { display: flex; justify-content: space-between; gap: 14px; }
.element-title-wrap { min-width: 0; display: flex; align-items: center; gap: 10px; }
.element-title-wrap h3 { margin: 0; overflow: hidden; font-size: 14px; text-overflow: ellipsis; white-space: nowrap; }
.reference-title-row { min-width: 0; display: flex; align-items: center; gap: 9px; }
.reference-title, .reference-search-links button { padding: 0; border: 0; background: transparent; font: inherit; cursor: pointer; }
.reference-title { max-width: 360px; overflow: hidden; color: #216b9d; font-size: 14px; font-weight: 700; text-align: left; text-decoration: underline; text-decoration-color: #b9cfdd; text-overflow: ellipsis; white-space: nowrap; text-underline-offset: 3px; }
.reference-title:hover { color: var(--accent-dark); text-decoration-color: var(--accent-dark); }
.reference-search-links { flex: none; display: flex; gap: 6px; }
.reference-search-links button { color: #698091; font-size: 11px; }
.reference-search-links button:hover { color: var(--accent-dark); }
.type-badge { flex: none; display: inline-flex; align-items: center; gap: 5px; padding: 4px 8px; border-radius: 4px; background: #eef8f4; color: #159767; font-size: 11px; }
.page-number { flex: none; color: #99a2ad; font-size: 11px; }
.element-content { max-height: 118px; margin-top: 13px; padding: 12px 14px; overflow: auto; border-radius: 6px; background: #f7f9fb; color: #5b6673; font-size: 12px; line-height: 20px; white-space: pre-wrap; }
.muted-content { color: #9ba4ae; }
.toc-list { margin: 13px 0 0; padding: 11px 14px; list-style: none; border-radius: 6px; background: #f7f9fb; color: #526071; font-size: 12px; }
.toc-list li { min-height: 25px; display: flex; align-items: baseline; gap: 8px; box-sizing: border-box; line-height: 20px; }
.toc-number { width: 34px; flex: none; color: var(--accent-dark); font-variant-numeric: tabular-nums; }
.toc-title { min-width: 0; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.toc-page { flex: none; color: #929ca8; font-variant-numeric: tabular-nums; }
.element-image-wrap { margin-top: 13px; }
.element-image-preview { width: 100%; max-height: 320px; display: block; object-fit: contain; border: 1px solid #e8edf1; border-radius: 7px; background: #f7f9fb; box-sizing: border-box; }
.element-image-state { min-height: 150px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 9px; border: 1px solid #e8edf1; border-radius: 7px; background: #f7f9fb; color: #7d8996; font-size: 12px; }
.element-image-state i { color: var(--accent); font-size: 24px; }
.element-image-caption { margin: 8px 2px 0; color: #737f8c; font-size: 12px; line-height: 19px; }
.artifact-list { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.artifact-list button { max-width: 260px; padding: 5px 9px; overflow: hidden; border: 1px solid #d9e4df; border-radius: 5px; background: #f8fcfa; color: #16865e; font: inherit; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; cursor: pointer; }
.artifact-list button:disabled { cursor: not-allowed; opacity: .45; }
.result-pagination { margin: 22px 0 10px; text-align: center; }

.history-panel { display: flex; flex-direction: column; background: #fff; border-left: 1px solid var(--line); }
.history-scroll { min-height: 0; flex: 1; overflow-y: auto; padding: 12px 10px 24px; }
.history-item { width: 100%; min-height: 80px; display: flex; gap: 11px; align-items: center; margin: 0 0 20px; padding: 9px; border: 1px solid transparent; border-radius: 8px; background: #fff; color: inherit; font: inherit; text-align: left; cursor: pointer; transition: background .15s ease, border-color .15s ease; }
.history-item:hover { background: #f6faf8; }
.history-item.active { border-color: #93d9bd; background: #eff9f5; box-shadow: 0 2px 8px rgba(23, 183, 123, .08); }
.history-thumbnail { position: relative; width: 100px; height: 64px; flex: none; display: grid; place-items: center; overflow: hidden; border-radius: 4px; background: #e9f1f7; }
.history-thumbnail img { width: 35px; max-height: 44px; object-fit: contain; }
.thumbnail-progress { position: absolute; right: 8px; bottom: 7px; left: 8px; height: 3px; overflow: hidden; border-radius: 2px; background: rgba(45, 70, 89, .18); }
.thumbnail-progress span { display: block; height: 100%; border-radius: 2px; background: var(--accent); }
.history-info { min-width: 0; flex: 1; display: flex; flex-direction: column; gap: 5px; }
.history-info strong, .history-info span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.history-info strong { color: #2f3740; font-size: 13px; }
.history-status { color: #8c96a2; font-size: 11px; }
.history-status.success { color: #6b8c7f; }.history-status.processing { color: #158b67; }.history-status.failed { color: #bc6b66; }
.history-info time { color: #a6adb6; font-size: 10px; }
.history-loading, .history-empty { min-height: 190px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; color: #a1aab5; font-size: 12px; }
.history-loading i, .history-empty i { color: #c6ced7; font-size: 24px; }
.history-loading.more { min-height: 38px; }.history-end { padding: 12px 0 4px; color: #b0b7bf; font-size: 10px; text-align: center; }

@media (max-width: 1180px) {
  .extract-workspace { grid-template-columns: 220px minmax(270px, 1fr) 210px; }
  .upload-panel { padding-right: 15px; padding-left: 15px; }
  .history-thumbnail { width: 82px; }
}

@media (max-width: 900px) {
  .material-extract-page { height: auto; min-height: 100vh; overflow: visible; }
  .material-extract-body { height: auto; min-height: 0; display: block; overflow: visible; }
  .material-extract-nav { display: none; }
  .material-extract-content { overflow: visible; }
  .extract-header { position: sticky; top: 56px; z-index: 5; padding: 0 14px; }
  .material-extract-footer { min-height: 128px; }
  .extract-brand > span:last-child, .header-divider { display: none; }
  .extract-workspace { height: auto; min-height: 0; grid-template-columns: 1fr; }
  .upload-panel, .result-panel, .history-panel { border: 0; border-bottom: 1px solid var(--line); }
  .upload-panel { overflow: visible; }
  .result-panel { min-height: 580px; }
  .result-body { min-height: 480px; overflow: visible; }
  .history-panel { min-height: 420px; }
  .history-scroll { max-height: 520px; }
  .history-thumbnail { width: 92px; }
}
</style>
